import React, { useEffect, useState } from "react";
import { isArray, isEmpty } from "lodash";
import { useParams, useNavigate } from "react-router-dom"; // Import useNavigate
import { Setting } from "../../utils/Setting";
import { getApiData } from "../../utils/APIHelper";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Modal,
  Backdrop,
  Stack,
  Typography,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/system";
import { statusStyleArray } from "./config";
import tabs from "./config";
import rawStyles from "./styles";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";
import Milestones from "./components/Milestones";
import Budget from "./components/Budget";
import PaymentGroups from "./components/PaymentGroups";
import ProjectOverview from "./components/ProjectOverview";
import Documents from "./components/Documents";
import { toast } from "react-toastify";
import ConfirmModel from "../../components/ConfirmModel";
import Images from "../../config/images";
import CInput from "../../components/CInput";
import { AddCircleOutline, DeleteOutline } from "@mui/icons-material";
import CommissionDetails from "./components/CommissionDetails";
import { ReactComponent as BlackSmallDhiramIcon } from "../../assets/svg/BlackSmallDhiramIcon.svg";
import { useTheme } from "@emotion/react";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import { renderCurrencyIcon } from "../../utils/CurrencyUtils";

const StyledTab = styled(Tab)(({ theme, selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d !important" : "#fff !important",
  border: "1px solid #d7d8d9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "4px 16px !important",
  fontFamily: "Inter Tight",
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "",
    color: !selected && "#000",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "transparent",
  },
}));

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

export default function NewProposalDetails() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Token validation hook
  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  const rejectModal = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? "90%" : "50%",
    height: "50%",
    bgcolor: "background.paper",
    px: 2,
    py: 2,
    overflow: "hidden",
  };

  const [loader, setLoader] = useState(false);
  const [details, setDetails] = useState({});
  const [isApproved, setIsApproved] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [notes, setNotes] = useState([{ note: "" }]);
  const [submitLoader, setSubmitLoader] = useState(false);

  const { id, tab } = useParams();
  const navigate = useNavigate();
  const styles = rawStyles(isMobile);

  // Define tab mapping
  const tabRoutes = [
    "",
    "milestone",
    "budget",
    "payment-group",
    "contract-details",
    "documents",
  ]; // Empty for tabs using main route

  // Determine active tab based on URL
  const tabValue = tabRoutes.indexOf(tab || "");

  useEffect(() => {
    getProjectDetails();
  }, [id]);

  async function getProjectDetails() {
    setLoader(true);
    try {
      const response = await getApiData(
        `${Setting.endpoints.getProject}?proposal_id=${id}`,
        "GET",
        {}
      );
      if (
        response.success &&
        isArray(response?.data) &&
        !isEmpty(response?.data)
      ) {
        setDetails(response?.data[0]);
      }
      setLoader(false);
    } catch (error) {
      console.log("error:===>>>", error);
      setLoader(false);
    }
  }

  function handleTabChange(event, newValue) {
    const newTabRoute = tabRoutes[newValue]
      ? `/proposal/details/${id}/${tabRoutes[newValue]}`
      : `/proposal/details/${id}`;

    navigate(newTabRoute);
  }

  async function handleApproveProject() {
    try {
      const response = await getApiData(
        `${Setting.endpoints.approveRejectProject}`,
        "PUT",
        { project_id: details?.id, status: "approve" }
      );
      if (response.success) {
        toast.success("Proposal Approved");
        setIsApproved(false);
        getProjectDetails();
      } else {
        toast.error(response.message);
        setIsApproved(false);
        getProjectDetails();
      }
    } catch (error) {
      toast.error(error);
    }
  }

  async function handleRejectProject() {
    try {
      const response = await getApiData(
        `${Setting.endpoints.approveRejectProject}`,
        "PUT",
        { project_id: details?.id, status: "reject" }
      );
      if (response.success) {
        toast.success("Proposal Rejected");
        setIsRejected(false);
        getProjectDetails();
      }
    } catch (error) {
      toast.error(error);
    }
  }
  async function handleSubmitNotes() {
    try {
      const response = await getApiData(`${Setting.endpoints.notes}`, "POST", {
        project_id: details?.id,
        notes: notes.filter((note) => note.note).map((item) => item.note),
      });
      if (response.success) {
        toast.success("Notes added successfully");
      }
    } catch (error) {
      toast.error(error);
    }
  }

  const handleSubmit = async () => {
    setSubmitLoader(true);
    if (notes[0].note === "") {
      toast.error("Notes should not be empty");
      setSubmitLoader(false);
    } else {
      await handleSubmitNotes();
      await handleRejectProject();
    }
  };

  const handleAddNote = () => {
    setNotes([...notes, ""]);
  };

  const handleDeleteNotes = (index) => {
    setNotes(notes.filter((value, newIndex) => newIndex !== index));
  };

  const handleNoteChange = (index, value) => {
    const updatedNotes = [...notes];
    updatedNotes[index] = { note: value };
    setNotes(updatedNotes);
  };

  function renderTabContent() {
    switch (tabValue) {
      case 0:
        return (
          <ProjectOverview
            details={details}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 1:
        return (
          <Milestones
            details={details}
            getProjectDetails={getProjectDetails}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 2:
        return <Budget details={details} isMobile={isMobile} styles={styles} getProjectDetails={getProjectDetails} />;
      case 3:
        return (
          <PaymentGroups
            details={details}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 4:
        return (
          <CommissionDetails
            details={details}
            isMobile={isMobile}
            styles={styles}
          />
        );
      case 5:
        return (
          <Documents details={details} isMobile={isMobile} styles={styles} />
        );
    }
  }

  return (
    <div style={{ height: "100%" }}>
      {/* Security Policy Modal */}
      <SecurityPolicyModal
        open={showSecurityModal}
        onConfirm={handleSecurityModalClose}
      />

      {loader ? (
        <center style={styles.header}>
          <CircularProgress />
        </center>
      ) : (
        <>
          <div style={styles.container}>
            <header
              style={{
                ...styles.header,
                ...(isMobile ? styles.columnFlex : styles.rowFlex),
              }}
            >
              <div
                style={{
                  ...styles.headerContent,
                  flex: 1,
                  gap: isMobile ? "10px" : "30px",
                }}
              >
                <LeftArrowIcon
                  onClick={() => navigate("/proposals")}
                  style={{ cursor: "pointer" }}
                />
                <div style={styles.idText}>
                  <span style={styles.idLabel}>ID:</span>{" "}
                  {details?.project_slug}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Box
                    style={{
                      width: "15px",
                      height: "15px",
                      borderRadius: "50%",
                      backgroundColor:
                        statusStyleArray.find(
                          (style) => style.status === details?.proposal_status
                        )?.backgroundColor || "transparent",
                      border: "3px solid #e5e5e5",
                    }}
                  />
                  <Typography
                    style={{
                      fontSize: "14px",
                      marginLeft: "8px",
                      color: "#0D0D0E",
                    }}
                  >
                    {getStatusDisplayText(details?.proposal_status)}
                  </Typography>
                </div>
              </div>
              {details?.proposal_status ===
                "awaiting-contractor-confirmation" && (
                  <div
                    style={{
                      ...styles.rowFlex,
                      flex: 1,
                      justifyContent: isMobile ? "flex-start" : "flex-end",
                      gap: "10px",
                      marginTop: isMobile && "1rem",
                    }}
                  >
                    <button
                      style={{
                        ...styles.milestoneBtn,
                        backgroundColor: "#e54746",
                      }}
                      onClick={() => setIsRejected(true)}
                    >
                      Reject
                    </button>
                    <button
                      style={{
                        ...styles.milestoneBtn,
                      }}
                      onClick={() => setIsApproved(true)}
                    >
                      Approve
                    </button>
                  </div>
                )}
            </header>

            <div style={styles.subContainer}>
              <div
                style={{
                  display: isMobile ? "flex" : "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile ? "16px" : "0",
                }}
              >
                <div
                  style={{
                    ...styles.rowFlex,
                    gap: "15px",
                    flex: isMobile ? "none" : "2",
                  }}
                >
                  <Box
                    component="img"
                    src={
                      details?.project_image?.[0]?.image
                        ? details?.project_image?.[0]?.image
                        : details?.default_image?.[0]?.image
                    }
                    alt="Project Icon"
                    sx={{
                      width: isMobile ? 70 : 90,
                      height: isMobile ? 70 : 90,
                      borderRadius: "16px",
                      marginRight: 1,
                    }}
                  />
                  <div
                    style={{
                      ...styles.columnFlex,
                      gap: isMobile ? "5px" : "10px",
                    }}
                  >
                    <div style={{ ...styles.rowFlex, gap: "15px" }}>
                      <p style={styles.startDate}>
                        Start Date: {details?.start_date}
                      </p>
                    </div>
                    <h2
                      style={{
                        ...styles.projectHeading,
                        fontSize: isMobile ? "18px" : "22px",
                      }}
                    >
                      {details?.name}
                    </h2>
                    <p style={styles.endDate}>
                      Moving Date: {details?.end_date}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    ...styles.columnFlex,
                    flex: isMobile ? "none" : "1",
                  }}
                >
                  <p style={styles.idLabel}>Cost</p>
                  <p style={styles.price}>
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      {renderCurrencyIcon('black-small')}
                      {formattedAmountWithoutAed(
                        details?.proposal_status !== "ongoing"
                          ? details?.budget
                          : details?.budget_without_commission
                      )}
                    </span>
                  </p>
                </div>
              </div>

              <Tabs
                value={tabValue}
                variant="scrollable"
                onChange={handleTabChange}
                sx={{
                  marginTop: "3vh",
                  "& .MuiTabs-indicator": {
                    background: "none",
                  },
                }}
              >
                {tabs.map((tab, index) => (
                  <StyledTab
                    key={tab.id}
                    label={tab.title}
                    selected={tabValue === index}
                  />
                ))}
              </Tabs>

              {renderTabContent()}
            </div>
          </div>
          {isRejected && (
            <Modal
              open={isRejected}
              onClose={() => setIsRejected(false)}
              closeAfterTransition
              disableAutoFocus
              slotProps={{ backdrop: Backdrop }}
            >
              <Box sx={rejectModal}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography style={styles.notes_heading}>
                    Additional Info
                  </Typography>
                  <Stack width="32px" height="32px" sx={{ cursor: "pointer" }}>
                    <img
                      src={Images.close}
                      onClick={() => setIsRejected(false)}
                      alt="close"
                      style={{ width: "100%", height: "100%" }}
                    ></img>
                  </Stack>
                </Stack>
                <Divider />

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography style={styles.notes_label}>Notes</Typography>
                  <Button
                    variant="contained"
                    onClick={handleAddNote}
                    style={{ alignSelf: "center", gap: "4px" }}
                  >
                    Add Note
                    <AddCircleOutline />
                  </Button>
                </Stack>
                <Stack
                  alignItems={"center"}
                  flex={1}
                  overflow="auto"
                  gap="16px"
                >
                  {notes.map((item, index) => {
                    return (
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <CInput
                          label={`Note ${index + 1}`}
                          value={item?.note}
                          multiline
                          onChange={(e) =>
                            handleNoteChange(index, e.target.value)
                          }
                        />
                        {notes.length > 1 && (
                          <DeleteOutline
                            onClick={() => handleDeleteNotes(index)}
                            sx={{ cursor: "pointer" }}
                          />
                        )}
                      </div>
                    );
                  })}
                </Stack>
                <Stack direction="row" gap="16px" alignSelf="end">
                  <Button
                    variant="outlined"
                    onClick={() => setIsRejected(false)}
                  >
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    endIcon={
                      submitLoader && (
                        <CircularProgress color="white" size={18} />
                      )
                    }
                  >
                    Submit
                  </Button>
                </Stack>
              </Box>
            </Modal>
          )}

          <ConfirmModel
            visible={isApproved}
            handleClose={() => setIsApproved(false)}
            confirmation={handleApproveProject}
            message={
              <>
                <p>Are you sure you want to approve this project?</p>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ flexWrap: "wrap", flex: 1, marginTop: "1vh" }}
                >
                  <Box
                    component="img"
                    src={
                      details?.project_image?.[0]?.image
                        ? details?.project_image?.[0]?.image
                        : details?.default_image?.[0]?.image
                    }
                    alt="Project Icon"
                    sx={{
                      width: 45,
                      height: 45,
                      borderRadius: "8px",
                      border: "2px solid white",
                      boxShadow: "0px 4px 2.67px 0px #00000026",
                      marginRight: 1,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      fontSize: "14px",
                      fontWeight: "400",
                    }}
                  >
                    {details?.name}
                  </Typography>
                </Box>
              </>
            }
          />
        </>
      )}
    </div>
  );
}
